import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import ImageSelector from '../components/ImageSelector';
import ColorPaletteSelector from '../components/ColorPaletteSelector';
import styles from '../styles/pages/SettingsPage.module.scss';

const SettingsPage = () => {
    const { currentUser } = useAuth();
    const [settings, setSettings] = useState({
        customHead: '',
        customBody: '',
        faviconUrl: '',
        snsShareImageUrl: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState('');
    const [showFaviconSelector, setShowFaviconSelector] = useState(false);
    const [showSnsImageSelector, setShowSnsImageSelector] = useState(false);
    const [activeTab, setActiveTab] = useState('colorPalette');

    useEffect(() => {
        const fetchSettings = async () => {
            if (!currentUser) return;

            try {
                const settingsDoc = await getDoc(doc(db, 'settings', currentUser.uid));
                if (settingsDoc.exists()) {
                    setSettings(settingsDoc.data());
                }
            } catch (error) {
                console.error('設定の取得に失敗しました:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [currentUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFaviconSelect = (url) => {
        setSettings(prev => ({
            ...prev,
            faviconUrl: url
        }));
    };

    const handleSnsImageSelect = (url) => {
        setSettings(prev => ({
            ...prev,
            snsShareImageUrl: url
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser) return;

        try {
            setSaveStatus('保存中...');
            await setDoc(doc(db, 'settings', currentUser.uid), settings);
            setSaveStatus('保存しました');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('設定の保存に失敗しました:', error);
            setSaveStatus('保存に失敗しました');
        }
    };

    const handleSaveColorPalette = async () => {
        if (!currentUser) return;
        try {
            setSaveStatus('保存中...');
            await setDoc(doc(db, 'settings', currentUser.uid), settings);
            setSaveStatus('カラーパレットを保存しました');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('カラーパレットの保存に失敗しました:', error);
            setSaveStatus('保存に失敗しました');
        }
    };

    const handleSaveCustomCode = async () => {
        if (!currentUser) return;
        try {
            setSaveStatus('保存中...');
            await setDoc(doc(db, 'settings', currentUser.uid), settings);
            setSaveStatus('カスタムコードを保存しました');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('カスタムコードの保存に失敗しました:', error);
            setSaveStatus('保存に失敗しました');
        }
    };

    const handleSaveMedia = async () => {
        if (!currentUser) return;
        try {
            setSaveStatus('保存中...');
            await setDoc(doc(db, 'settings', currentUser.uid), settings);
            setSaveStatus('メディア設定を保存しました');
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            console.error('メディア設定の保存に失敗しました:', error);
            setSaveStatus('保存に失敗しました');
        }
    };

    if (isLoading) {
        return (
            <div>
                <Header />
                <div className={styles.loading}>読み込み中...</div>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <div className={styles.container}>
                <div className={styles.settingsLayout}>
                    {/* 左側のナビゲーション */}
                    <div className={styles.sidebar}>
                        <nav className={styles.nav}>
                            <button
                                className={`${styles.navButton} ${activeTab === 'colorPalette' ? styles.active : ''}`}
                                onClick={() => setActiveTab('colorPalette')}
                            >
                                カラーパレット
                            </button>
                            <button
                                className={`${styles.navButton} ${activeTab === 'customCode' ? styles.active : ''}`}
                                onClick={() => setActiveTab('customCode')}
                            >
                                カスタムhead&body
                            </button>
                            <button
                                className={`${styles.navButton} ${activeTab === 'media' ? styles.active : ''}`}
                                onClick={() => setActiveTab('media')}
                            >
                                Favicon & SNSシェア画像
                            </button>
                        </nav>
                    </div>

                    {/* 右側のコンテンツ */}
                    <div className={styles.content}>
                        {activeTab === 'colorPalette' && (
                            <div className={styles.tabContent}>
                                <h2>カラーパレット設定</h2>
                                <ColorPaletteSelector />
                                <div className={styles.saveSection}>
                                    <button
                                        type="button"
                                        onClick={handleSaveColorPalette}
                                        className={styles.saveButton}
                                    >
                                        保存
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'customCode' && (
                            <div className={styles.tabContent}>
                                <h2>カスタムhead&body設定</h2>
                                <form onSubmit={handleSubmit} className={styles.form}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="customHead">
                                            カスタムhead情報
                                        </label>
                                        <textarea
                                            id="customHead"
                                            name="customHead"
                                            value={settings.customHead}
                                            onChange={handleInputChange}
                                            rows={12}
                                            className={styles.textareaLarge}
                                        />
                                        <small>
                                            すべてのページのhead要素に挿入されるHTMLコードを入力してください。
                                            Google Analytics、Google Tag Manager、その他のトラッキングコードやメタタグを含めることができます。
                                        </small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="customBody">
                                            カスタムbody情報
                                        </label>
                                        <textarea
                                            id="customBody"
                                            name="customBody"
                                            value={settings.customBody}
                                            onChange={handleInputChange}
                                            rows={10}
                                            className={styles.textareaLarge}
                                        />
                                        <small>
                                            すべてのページのbody要素の開始直後に挿入されるHTMLコードを入力してください。
                                            Google Tag Manager (noscript)、チャットボット、その他のスクリプトやウィジェットを含めることができます。
                                        </small>
                                    </div>

                                    <div className={styles.saveSection}>
                                        <button
                                            type="button"
                                            onClick={handleSaveCustomCode}
                                            className={styles.saveButton}
                                        >
                                            保存
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {activeTab === 'media' && (
                            <div className={styles.tabContent}>
                                <h2>Favicon & SNSシェア画像設定</h2>
                                <form onSubmit={handleSubmit} className={styles.form}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="faviconUrl">
                                            Favicon URL
                                        </label>
                                        <div className={styles.inputGroup}>
                                            <input
                                                type="text"
                                                id="faviconUrl"
                                                name="faviconUrl"
                                                value={settings.faviconUrl}
                                                onChange={handleInputChange}
                                                placeholder="https://example.com/favicon.ico"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowFaviconSelector(true)}
                                                className={styles.selectButton}
                                            >
                                                選択
                                            </button>
                                        </div>
                                        {settings.faviconUrl && (
                                            <div className={styles.faviconPreview}>
                                                <p>プレビュー:</p>
                                                <img
                                                    src={settings.faviconUrl}
                                                    alt="Favicon Preview"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'block';
                                                    }}
                                                />
                                                <span className={styles.faviconError} style={{ display: 'none' }}>
                                                    画像の読み込みに失敗しました
                                                </span>
                                            </div>
                                        )}
                                        <small>ファビコンのURLを入力してください。.ico、.png、.svg形式をサポートしています。</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="snsShareImageUrl">
                                            SNSシェア画像のURL
                                        </label>
                                        <div className={styles.inputGroup}>
                                            <input
                                                type="text"
                                                id="snsShareImageUrl"
                                                name="snsShareImageUrl"
                                                value={settings.snsShareImageUrl}
                                                onChange={handleInputChange}
                                                placeholder="https://example.com/sns-share-image.jpg"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowSnsImageSelector(true)}
                                                className={styles.selectButton}
                                            >
                                                選択
                                            </button>
                                        </div>
                                        {settings.snsShareImageUrl && (
                                            <div className={styles.snsShareImagePreview}>
                                                <p>プレビュー:</p>
                                                <img
                                                    src={settings.snsShareImageUrl}
                                                    alt="SNS Share Image Preview"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'block';
                                                    }}
                                                />
                                                <span className={styles.snsShareImageError} style={{ display: 'none' }}>
                                                    画像の読み込みに失敗しました
                                                </span>
                                            </div>
                                        )}
                                        <small>SNSシェア画像のURLを入力してください。.jpg、.png形式をサポートしています。</small>
                                    </div>

                                    <div className={styles.saveSection}>
                                        <button
                                            type="button"
                                            onClick={handleSaveMedia}
                                            className={styles.saveButton}
                                        >
                                            保存
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {saveStatus && (
                            <div className={styles.status}>
                                {saveStatus}
                            </div>
                        )}
                    </div>
                </div>

                {/* 画像選択モーダル */}
                <ImageSelector
                    isOpen={showFaviconSelector}
                    onClose={() => setShowFaviconSelector(false)}
                    onSelect={handleFaviconSelect}
                    currentValue={settings.faviconUrl}
                />

                <ImageSelector
                    isOpen={showSnsImageSelector}
                    onClose={() => setShowSnsImageSelector(false)}
                    onSelect={handleSnsImageSelect}
                    currentValue={settings.snsShareImageUrl}
                />
            </div>
        </div>
    );
};

export default SettingsPage;
